import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// PHASE 3.1: Import centralized constants
import { STORAGE_KEYS, TIMING, API_ENDPOINTS, DEVICE_INFO, ERROR_MESSAGES, isPublicRoute } from './auth-constants';
import { UNAUTHORIZED_EVENT } from '@/lib/auth-events';

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  provider: 'email' | 'google' | 'microsoft' | 'apple';
  role?: string;
  supabase_user_id: string;
}

// Exported for Storybook mock providers only — do not import in production code.
//
// ANA-16: the session lives entirely in httpOnly `sb-*` cookies. The client never
// sees, stores, or forwards access/refresh tokens — so the context exposes no
// token fields and there is no client-side refresh (Supabase rotates the access
// cookie server-side on the next request; see requireUnifiedAuth).
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isValidating: boolean;
  /** True when a previously-stored session was killed by a 401 (timed out vs. never logged in). */
  sessionExpired: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  socialLogin: (provider: 'google' | 'microsoft' | 'apple', redirectUri: string) => Promise<void>;
  logout: (options?: { allPlatforms?: boolean; platform?: string }) => Promise<void>;
  validateSession: () => Promise<boolean>;
  clearError: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  confirm_password: string;
  full_name?: string;
}

interface AuthResponse {
  success: boolean;
  user: User;
}

// Exported for Storybook mock providers only — do not import in production code.
export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.USER);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // PHASE 1.1: Optimistic Authentication — only validate if we have a stored user.
  // Cache-aware: we only enter the "validating" state (which gates protected content
  // via ProtectedRoute) when there is a stored user AND the cached validation has
  // expired. A recently validated session keeps the optimistic fast-path; a stale
  // one blocks until /auth/me reconciles, so authenticated data fetches don't race
  // ahead and 401 (home error-on-first-load bug).
  const [isValidating, setIsValidating] = useState(() => {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    if (!storedUser) return false; // Nothing to validate
    const lastValidation = localStorage.getItem(STORAGE_KEYS.LAST_VALIDATION);
    const lastValidationTime = lastValidation ? parseInt(lastValidation, 10) : 0;
    return Date.now() - lastValidationTime >= TIMING.VALIDATION_CACHE_DURATION;
  });
  const queryClient = useQueryClient();
  // Re-entrancy guard so a burst of 401s (e.g. several queries refetching on
  // window focus against a dead session) collapses into a single /auth/me check.
  const unauthorizedCheckRef = useRef(false);
  // Set when a signal arrives mid-confirm so we can re-confirm if the in-flight
  // attempt ended inconclusively (network error / 5xx) rather than dropping it.
  const unauthorizedPendingRef = useRef(false);

  const isAuthenticated = !!user; // Session is in httpOnly cookies — user state is the signal.

  // Persist only the (non-sensitive) user profile for optimistic hydration — never
  // tokens (FR-004). The session itself is the httpOnly sb-* cookies.
  const storeAuthData = (authData: AuthResponse) => {
    setUser(authData.user);
    setSessionExpired(false);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(authData.user));
    localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, Date.now().toString());
  };

  const clearAuthData = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.LAST_VALIDATION);
    // ANA-177: Preserve profile-settings (non-sensitive UI preferences:
    // inputMode, theme, layout prefs) so they don't flash as missing during
    // brief token rotations that produce a transient 401 from /auth/me.
    //
    // Save before clear, re-seed after: queryClient.clear() cancels all
    // observers (no stale-fetch triggers), then we put the preferences back as
    // fresh data. Using removeQueries instead would trigger background refetches
    // on active query observers — that caused 3 extra fetch calls in T017a.
    //
    // Note: useProfileSettings uses a global key ['v2', 'profile-settings'] not
    // scoped to a user id. After a user switch, the stale data will be visible
    // briefly until the next fetch completes — acceptable because the data
    // contains no sensitive identity info.
    const profileData = queryClient.getQueryData(['v2', 'profile-settings']);
    queryClient.clear();
    if (profileData !== undefined) {
      queryClient.setQueryData(['v2', 'profile-settings'], profileData);
    }
  }, [queryClient]);

  const clearError = () => setError(null);

  // Hydrate + reconcile auth state on mount, and sync across tabs.
  useEffect(() => {
    // Non-blocking background reconcile of the cached identity against /auth/me.
    // The optimistic fast-path renders instantly from the cached user; without this,
    // a user cached BEFORE a later profile change (e.g. the Google avatar synced on a
    // subsequent login) stays stale until the validation-cache window expires. This
    // only ADOPTS fresh server identity on success and never logs out here (a real
    // 401 is handled by the full cache-expired path on the next load), so it can't
    // re-enter the blocking `isValidating` state — the perf fast-path is preserved.
    const reconcileUserInBackground = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.AUTH_ME, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.user) {
            setUser(data.user);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
            localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, Date.now().toString());
          }
        }
        // Non-ok (incl. 401): keep the optimistic cached user untouched.
      } catch {
        // Network error — keep the cached user.
      }
    };

    const validateStoredAuth = async () => {
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

      if (storedUser) {
        // PHASE 2.1: validation cache — skip the server round-trip if recent.
        const lastValidation = localStorage.getItem(STORAGE_KEYS.LAST_VALIDATION);
        const lastValidationTime = lastValidation ? parseInt(lastValidation, 10) : 0;

        if (Date.now() - lastValidationTime < TIMING.VALIDATION_CACHE_DURATION) {
          try {
            const parsedUser: User = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsValidating(false);
            // Render instantly from cache, then refresh identity in the background
            // so a stale cached avatar/name/role can't persist for the cache window.
            void reconcileUserInBackground();
            return;
          } catch {
            // Corrupted stored user — fall through to server validation.
          }
        }

        // Restore optimistically from localStorage, then reconcile with the server.
        let parsedUser: User;
        try {
          parsedUser = JSON.parse(storedUser) as User;
        } catch {
          setIsValidating(false);
          return;
        }
        setUser(parsedUser);

        try {
          const response = await fetch(API_ENDPOINTS.AUTH_ME, {
            method: 'GET',
            credentials: 'include', // httpOnly sb-* cookies
          });

          if (response && response.ok) {
            const data = await response.json();
            // ANA-29 (1b): always adopt the server's identity. If the session cookie
            // belongs to a different account than the cached user, switch to it —
            // never keep rendering the previous identity (cross-account stale bug).
            if (data?.user) {
              setUser(data.user);
              localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
            }
            localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, Date.now().toString());
          } else if (response && response.status === 401) {
            // Session is dead. There is no client-side refresh (FR-005) — degrade to
            // the login flow (FR-009): clear the cached identity so we never show a
            // stale/foreign user. Supabase rotates a still-valid session server-side,
            // so a 401 here means the session is genuinely gone.
            // Mark as expired (not "never logged in") so ProtectedRoute can show the
            // "session timed out" screen with a path-preserving sign-in link.
            setSessionExpired(true);
            clearAuthData();
          }
          // Other non-OK (transient 5xx): keep the optimistic user — don't lock out.
        } catch {
          // Network error — keep the user logged in optimistically (no lockout).
        }
      } else {
        // No stored user. Skip /auth/me on public routes (guest invite, auth pages)
        // to avoid unnecessary 401s and network noise.
        if (!isPublicRoute()) {
          try {
            const response = await fetch(API_ENDPOINTS.AUTH_ME, {
              method: 'GET',
              credentials: 'include',
            });

            if (response.ok) {
              const data = await response.json();
              localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, Date.now().toString());
              setUser(data.user);
            }
          } catch {
            // No cookies or network error; user remains unauthenticated.
          }
        }
      }
      setIsValidating(false);
    };

    validateStoredAuth();

    // Cross-tab sync: adopt a freshly-stored identity, and clear on a logout event.
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.USER && e.newValue) {
        try {
          // ANA-29: adopt the freshly-stored identity even with no tokens in
          // localStorage — the session lives in httpOnly cookies. (Picks up the
          // post-OAuth update dispatched by AuthSuccess in this and other tabs.)
          setUser(JSON.parse(e.newValue));
          setSessionExpired(false);
          setIsValidating(false);
          setError(null);
        } catch (err) {
          console.error('Failed to parse user data:', err);
        }
      } else if (e.key === STORAGE_KEYS.LOGOUT_EVENT && e.newValue) {
        try {
          const logoutEvent = JSON.parse(e.newValue);
          clearAuthData();
          setError(
            logoutEvent.type === 'cross_platform'
              ? ERROR_MESSAGES.LOGOUT_ALL_PLATFORMS
              : ERROR_MESSAGES.LOGOUT_ANOTHER_TAB
          );
        } catch (err) {
          console.error('Failed to parse logout event:', err);
          clearAuthData();
          setError(ERROR_MESSAGES.AUTH_SESSION_EXPIRED);
        }
      } else if (e.key === STORAGE_KEYS.USER && !e.newValue) {
        clearAuthData();
        setError(ERROR_MESSAGES.AUTH_SESSION_EXPIRED);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ANA — auto-route a timed-out session to the re-auth surface.
  //
  // Previously a 401 from an authenticated *data* fetch (e.g. GET /projects)
  // never touched auth state: the app stayed optimistically authenticated and
  // the user was stuck inside the platform with inline "Couldn't load…" errors,
  // only escaping after a manual reload re-ran the mount-time /auth/me check.
  //
  // Now any authenticated request that 401s broadcasts UNAUTHORIZED_EVENT (see
  // lib/auth-events + lib/queryClient). We confirm with a single /auth/me call —
  // a genuinely dead session 401s again ⇒ flip `sessionExpired` + clear, so
  // ProtectedRoute swaps the page to AuthSurface variant="expired" ("Your
  // session has timed out"). Confirming (rather than logging out on the first
  // 401) tolerates a transient 401 during server-side cookie rotation: if
  // /auth/me is OK the session is fine and the original query simply refetches.
  useEffect(() => {
    const handleUnauthorized = async () => {
      // Only act if we currently believe we're authenticated. Reading
      // localStorage keeps this correct without re-subscribing on every user
      // change (the effect mounts once). Skips public/login flows entirely.
      if (!localStorage.getItem(STORAGE_KEYS.USER)) return;
      if (unauthorizedCheckRef.current) {
        // A confirm is already in flight — remember the signal so we can
        // re-check if that confirm turns out inconclusive (don't drop it).
        unauthorizedPendingRef.current = true;
        return;
      }
      unauthorizedCheckRef.current = true;
      // Inconclusive = we couldn't decide the session's fate (network error /
      // 5xx), so the auth state is left as-is and a queued signal must retry.
      let inconclusive = false;
      try {
        const response = await fetch(API_ENDPOINTS.AUTH_ME, {
          method: 'GET',
          credentials: 'include',
        });
        if (response.status === 401) {
          // Session is genuinely gone — degrade to the timed-out re-auth surface.
          setSessionExpired(true);
          clearAuthData();
        } else if (response.ok) {
          // Transient 401 (cookie mid-rotation) — session is actually fine. Adopt
          // the fresh identity…
          const data = await response.json().catch(() => null);
          if (data?.user) {
            setUser(data.user);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
            localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, Date.now().toString());
          }
          // …and refetch the queries that 401'd while the session was briefly
          // dead. Without this, hooks with `retry: false` (or that already
          // exhausted retries) stay stuck in `isError` showing inline fetch
          // errors even though the session has recovered.
          void queryClient.invalidateQueries();
        } else {
          // Other non-OK (5xx): leave auth intact — never lock out on a server
          // blip — but allow a queued signal to retry.
          inconclusive = true;
        }
      } catch {
        // Network error — keep the optimistic user; no lockout. Inconclusive,
        // so a signal that arrived mid-flight should retry.
        inconclusive = true;
      } finally {
        unauthorizedCheckRef.current = false;
      }
      // If signals arrived while this confirm was in flight and we couldn't
      // resolve the session, re-run so the dropped 401 isn't lost. A conclusive
      // result (401 → logged out, or OK → invalidated) already handled them.
      const hadPending = unauthorizedPendingRef.current;
      unauthorizedPendingRef.current = false;
      if (hadPending && inconclusive) void handleUnauthorized();
    };

    const listener = () => {
      void handleUnauthorized();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, listener);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, listener);
  }, [clearAuthData, queryClient]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      try {
        const response = await fetch(API_ENDPOINTS.AUTH_LOGIN, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // server sets the sb-* session cookies
          body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          const msg = errorData?.message?.trim();
          const fallback = 'Invalid email or password. Please try again.';
          throw new Error(msg && msg !== '{}' ? msg : fallback);
        }

        return response.json() as Promise<AuthResponse>;
      } catch (error: any) {
        const msg = error?.message?.trim();
        const fallback = 'Login failed. Please check your credentials and try again.';
        throw new Error(msg && msg !== '{}' ? msg : fallback);
      }
    },
    onSuccess: (data) => {
      storeAuthData(data);
      setError(null);
    },
    onError: (error: Error) => {
      console.error('Login failed:', error);
      setError(error.message);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData) => {
      try {
        const response = await fetch(API_ENDPOINTS.AUTH_REGISTER, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // server sets the sb-* session cookies
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Registration failed');
        }

        return response.json() as Promise<AuthResponse>;
      } catch (error: any) {
        const errorMessage = error.message || 'Registration failed. Please try again.';
        throw new Error(errorMessage);
      }
    },
    onSuccess: (data) => {
      storeAuthData(data);
      setError(null);
    },
    onError: (error: Error) => {
      console.error('Registration failed:', error);
      setError(error.message);
    },
  });

  // Social login mutation — initiates OAuth on the server, which sets the PKCE
  // verifier cookie (credentials: 'include' so the browser stores it), then we
  // navigate to the provider. The session cookies are set on the callback.
  const socialLoginMutation = useMutation({
    mutationFn: async ({ provider, redirectUri }: { provider: string; redirectUri: string }) => {
      const response = await fetch(API_ENDPOINTS.SOCIAL_LOGIN(provider), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ redirect_uri: redirectUri }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Social login failed');
      }

      return response.json();
    },
    onSuccess: (data) => {
      window.location.href = data.oauth_url;
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Logout mutation (platform-specific or cross-platform). Cookie-only — the
  // server clears the sb-* session cookies; no Authorization header to send.
  const logoutMutation = useMutation({
    mutationFn: async (options?: { allPlatforms?: boolean; platform?: string }) => {
      const endpoint = options?.allPlatforms ? '/api/v1/auth/logout-all' : API_ENDPOINTS.AUTH_LOGOUT;
      const body = options?.allPlatforms ? {} : { platform: options?.platform || DEVICE_INFO.WEB_PLATFORM };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Logout failed');
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      const logoutEvent = {
        type: variables?.allPlatforms ? 'cross_platform' : 'platform_specific',
        platform: variables?.platform || DEVICE_INFO.WEB_PLATFORM,
        timestamp: Date.now(),
        message: variables?.allPlatforms ? 'Logged out from all platforms' : 'Logged out successfully',
      };

      localStorage.setItem(STORAGE_KEYS.LOGOUT_EVENT, JSON.stringify(logoutEvent));

      clearAuthData();
      setSessionExpired(false);
      setError(null);

      setTimeout(() => {
        localStorage.removeItem(STORAGE_KEYS.LOGOUT_EVENT);
      }, TIMING.LOGOUT_EVENT_CLEANUP_DELAY);
    },
    onError: (error: Error) => {
      console.error('❌ Logout failed:', error);
      // Even if logout fails on the server, clear local state.
      clearAuthData();
      setError(error.message);
    },
  });

  const isLoading =
    isValidating ||
    loginMutation.isPending ||
    registerMutation.isPending ||
    socialLoginMutation.isPending ||
    logoutMutation.isPending;

  // Re-check the session against the server (cookie-only). On an invalid session
  // (401) degrade to the "session timed out" re-auth surface (FR-009): set
  // sessionExpired so ProtectedRoute shows AuthSurface variant="expired" with a
  // path-preserving sign-in link (ANA-313). On success, refresh LAST_VALIDATION
  // so the next page load skips the blocking validation round-trip (ANA-314).
  const validateSession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH_ME, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          setUser(data.user);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
          localStorage.setItem(STORAGE_KEYS.LAST_VALIDATION, Date.now().toString());
        }
        return true;
      }

      if (response.status === 401) {
        setSessionExpired(true);
      }
      clearAuthData();
      return false;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }, [clearAuthData]);

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isValidating,
    sessionExpired,
    error,
    login: async (username: string, password: string) => {
      try {
        await loginMutation.mutateAsync({ username, password });
      } catch {
        // Error already surfaced via onError.
      }
    },
    register: async (data: RegisterData) => {
      try {
        await registerMutation.mutateAsync(data);
      } catch {
        // Error already surfaced via onError.
      }
    },
    socialLogin: async (provider: 'google' | 'microsoft' | 'apple', redirectUri: string) => {
      await socialLoginMutation.mutateAsync({ provider, redirectUri });
    },
    logout: async (options?: { allPlatforms?: boolean; platform?: string }) => {
      await logoutMutation.mutateAsync(options);
    },
    validateSession,
    clearError,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(ERROR_MESSAGES.MUST_USE_WITHIN_PROVIDER);
  }
  return context;
}
